import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Lightbulb, ClipboardList, Zap } from 'lucide-react';
import { PollarBrandMini } from '@/components/PollarBrand';
import { Card, CardContent } from '@/components/ui/card';

const SessionTypeSelector = () => {
  const navigate = useNavigate();

  useEffect(() => {
    // Check authentication
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login?signup=true');
      return;
    }
  }, [navigate]);

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #e8eef3 100%)' }}>
      <div className="container mx-auto px-4 py-8 max-w-5xl">
        <div className="flex justify-between items-center mb-4">
          <PollarBrandMini />
        </div>
        
        <Button
          onClick={() => navigate('/dashboard')}
          variant="ghost"
          className="mb-6"
        >
          <ArrowLeft className="mr-2 h-5 w-5" />
          Back to Dashboard
        </Button>

        <div className="bg-white rounded-2xl shadow-xl p-8">
          <h1 className="text-4xl font-bold mb-3 text-center" style={{ color: '#1a1a1a' }}>
            Choose Session Type
          </h1>
          <p className="text-gray-600 mb-12 text-center text-lg">
            Select the type of session you want to create
          </p>

          <div className="grid md:grid-cols-3 gap-6">
            {/* Idea Pool Card */}
            <Card 
              className="border-2 hover:border-purple-500 transition-all cursor-pointer group flex flex-col"
              onClick={() => navigate('/session-setup/idea-pool')}
            >
              <CardContent className="p-8 text-center flex flex-col flex-1">
                <div className="mb-6 flex justify-center">
                  <div className="w-24 h-24 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Lightbulb className="w-12 h-12 text-white" />
                  </div>
                </div>
                
                <h2 className="text-2xl font-bold mb-3" style={{ color: '#1a1a1a' }}>
                  Idea Pool
                </h2>
                
                <p className="text-gray-600 mb-6">
                  Collaborative brainstorming sessions where participants submit ideas and AI clusters them into meaningful themes
                </p>

                <div className="space-y-2 text-left text-sm text-gray-700 mb-6 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-purple-500"></span>
                    <span>Real-time idea submission</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-purple-500"></span>
                    <span>AI-powered clustering</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-purple-500"></span>
                    <span>Idea ranking & voting</span>
                  </div>
                </div>

                <Button 
                  className="w-full"
                  style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
                >
                  Create Idea Pool
                </Button>
              </CardContent>
            </Card>

            {/* Questionnaire Card */}
            <Card 
              className="border-2 hover:border-blue-500 transition-all cursor-pointer group flex flex-col"
              onClick={() => navigate('/session-setup/questionnaire')}
            >
              <CardContent className="p-8 text-center flex flex-col flex-1">
                <div className="mb-6 flex justify-center">
                  <div className="w-24 h-24 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <ClipboardList className="w-12 h-12 text-white" />
                  </div>
                </div>
                
                <h2 className="text-2xl font-bold mb-3" style={{ color: '#1a1a1a' }}>
                  Questionnaire / Survey
                </h2>
                
                <p className="text-gray-600 mb-6">
                  Create surveys and questionnaires with multiple question types, scoring, and detailed analytics
                </p>

                <div className="space-y-2 text-left text-sm text-gray-700 mb-6 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                    <span>Multiple Choice Question</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                    <span>Star Rating</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                    <span>Ranking of Options</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                    <span>Scoring of Answers</span>
                  </div>
                </div>

                <Button 
                  className="w-full"
                  style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #06b6d4 100%)' }}
                >
                  Create Questionnaire
                </Button>
              </CardContent>
            </Card>


            {/* Speed Run Quiz Card */}
            <Card 
              className="border-2 hover:border-orange-500 transition-all cursor-pointer group flex flex-col"
              onClick={() => navigate('/session-setup/speed-run-quiz')}
            >
              <CardContent className="p-8 text-center flex flex-col flex-1">
                <div className="mb-6 flex justify-center">
                  <div className="w-24 h-24 rounded-full bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Zap className="w-12 h-12 text-white" />
                  </div>
                </div>
                
                <h2 className="text-2xl font-bold mb-3" style={{ color: '#1a1a1a' }}>
                  Speed Run Quiz
                </h2>
                
                <p className="text-gray-600 mb-6">
                  Race against time! Participants compete to answer questions correctly as fast as possible
                </p>

                <div className="space-y-2 text-left text-sm text-gray-700 mb-6 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-orange-500"></span>
                    <span>Timed questions with countdown</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-orange-500"></span>
                    <span>Points for correct, deduct for wrong</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-orange-500"></span>
                    <span>Live leaderboard & rankings</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-orange-500"></span>
                    <span>Fast-paced competition</span>
                  </div>
                </div>

                <Button 
                  className="w-full"
                  style={{ background: 'linear-gradient(135deg, #f97316 0%, #dc2626 100%)' }}
                >
                  Create Speed Quiz
                </Button>
              </CardContent>
            </Card>



          </div>

          {/* Video Tutorial Section */}
          <div className="mt-16 pt-12 border-t border-gray-200">
            <div className="text-center mb-8">
              <h3 className="text-3xl font-bold mb-3" style={{ color: '#1a1a1a' }}>
                📺 How to Create an Idea Pool Session
              </h3>
              <p className="text-lg text-gray-600">
                Watch this quick tutorial to learn how to set up and run your first Idea Pool session
              </p>
            </div>
            <div className="max-w-4xl mx-auto">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl bg-black" style={{ paddingBottom: '56.25%' }}>
                <iframe
                  className="absolute top-0 left-0 w-full h-full"
                  src="https://www.youtube.com/embed/ew7t1tGvM10"
                  title="Pollar Idea Pool Tutorial"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              </div>
              <p className="text-center text-sm text-gray-500 mt-4">
                💡 Pro tip: The entire session type card above is clickable to get started!
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SessionTypeSelector;
